<template>
    <Modal
        v-model="modalData.show"
        title="审核"
        class-name="vertical-center-modal"
        width="600"
        :loading="isLoading"
        :mask-closable="false"
    >
        <Spin v-if="isLoading" size="large" fix class="custom-spin">
            <Icon type="ios-loading" class="spin-icon"></Icon>
            <div>提交中...</div>
        </Spin>

        <Form
            ref="approveForm"
            :model="modalData"
            :rules="formRules"
            label-position="top"
            class="audit-form"
        >
            <div class="form-content">
                <FormItem
                    label="审核结果"
                    prop="approveStatus"
                    class="status-item"
                >
                    <RadioGroup
                        v-model="modalData.approveStatus"
                        class="status-radio-group"
                    >
                        <Radio label="通过" class="radio-item success">
                            <span>通过</span>
                        </Radio>
                        <Radio label="驳回" class="radio-item error">
                            <span>驳回</span>
                        </Radio>
                    </RadioGroup>
                </FormItem>

                <FormItem
                    label="审核备注"
                    prop="approveRemark"
                    :rules="remarkRules"
                    class="remark-item"
                >
                    <Input
                        v-model="modalData.approveRemark"
                        type="textarea"
                        :rows="4"
                        placeholder="请输入审核意见（驳回时必填）"
                        class="remark-textarea"
                        show-word-limit
                        :maxlength="200"
                    />
                </FormItem>
            </div>
        </Form>

        <div slot="footer" class="modal-footer">
            <Button type="text" @click="close" class="cancel-btn">取消</Button>
            <Button
                :type="confirmBtnType"
                :loading="isLoading"
                :disabled="isSubmitDisabled"
                @click="handleSubmit"
                class="confirm-btn"
                >确定</Button
            >
        </div>
    </Modal>
</template>

<script>
export default {
    name: "AuditModal",
    data() {
        return {
            isLoading: false,
            debounceTimer: null,
            isSubmitting: false,
            modalData: {
                id: "",
                show: false,
                approveStatus: "通过",
                approveRemark: ""
            },
            formRules: {
                approveStatus: [
                    {
                        required: true,
                        message: "请选择审核结果",
                        trigger: "change"
                    }
                ],
                approveRemark: [
                    {
                        validator: (rule, value, callback) => {
                            if (
                                this.modalData.approveStatus === "驳回"
                                && !value.trim()
                            ) {
                                callback(new Error("驳回时必须填写审核意见"));
                            }
                            else {
                                callback();
                            }
                        },
                        trigger: "blur"
                    }
                ]
            }
        };
    },
    computed: {
        remarkRules() {
            return [
                {
                    required: this.modalData.approveStatus === "驳回",
                    message: "驳回时必须填写审核意见",
                    trigger: "blur"
                }
            ];
        },
        confirmBtnType() {
            return this.modalData.approveStatus === "通过"
                ? "primary"
                : "error";
        },
        isSubmitDisabled() {
            return this.isLoading || this.isSubmitting;
        }
    },
    methods: {
        showModal(id) {
            this.modalData.show = true;
            this.modalData.id = id;
            this.resetForm();
            this.clearDebounceTimer();
        },
        resetForm() {
            if (this.$refs.approveForm) {
                this.$refs.approveForm.resetFields();
            }
        },
        handleSubmit() {
            if (this.isSubmitDisabled) {
                return;
            }
            
            this.clearDebounceTimer();
            
            this.debounceTimer = setTimeout(() => {
                this.$refs.approveForm.validate((valid) => {
                    if (valid) {
                        this.submitAudit();
                    }
                });
            }, 300);
        },
        async submitAudit() {
            this.isSubmitting = true;
            this.isLoading = true;
            
            try {
                await this.$emit(
                    "audit",
                    this.modalData.approveStatus,
                    this.modalData.approveRemark,
                    this.modalData.id
                );
                this.modalData.show = false;
                this.$Message.success("审核操作成功");
            }
            catch (error) {
                this.$Message.error("审核操作失败：" + (error.message || "未知错误"));
            }
            finally {
                this.isSubmitting = false;
                this.isLoading = false;
                this.clearDebounceTimer();
            }
        },
        close() {
            if (!this.isLoading) {
                this.modalData.show = false;
                this.clearDebounceTimer();
            }
        },
        clearDebounceTimer() {
            if (this.debounceTimer) {
                clearTimeout(this.debounceTimer);
                this.debounceTimer = null;
            }
        }
    }
};
</script>

<style scoped>
.vertical-center-modal {
    /deep/ .ivu-modal {
        top: 45%;
        border-radius: 12px;
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
        overflow: hidden;

        &-header {
            background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
            border-bottom: 1px solid #dee2e6;
            padding: 20px 24px;

            .ivu-modal-header-inner {
                font-size: 18px;
                color: #2d3436;
                font-weight: 600;
            }
        }

        &-body {
            padding: 28px 24px;
            background-color: #fafbfc;
        }
    }
}

/* 表单 */
.audit-form {
    .form-content {
        margin: 20px 0;
        padding: 20px;
        background: white;
        border-radius: 10px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    }

    .status-item {
        margin-bottom: 28px;

        .status-radio-group {
            width: 100%;
            display: flex;
            gap: 12px;

            .radio-item {
                flex: 1;
                text-align: center;
                margin: 0;
                border-radius: 10px;
                transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                border: 2px solid #e9ecef;
                padding: 12px 0;
                cursor: pointer;

                /deep/ span {
                    padding: 0 16px;
                    font-weight: 500;
                    font-size: 15px;
                }

                &.success.ivu-radio-checked {
                    background: linear-gradient(135deg, #f0fff4 0%, #e6fffa 100%);
                    border-color: #38a169;
                    color: #38a169;
                    transform: translateY(-2px);
                    box-shadow: 0 4px 12px rgba(56, 161, 105, 0.2);
                }

                &.error.ivu-radio-checked {
                    background: linear-gradient(135deg, #fff5f5 0%, #fed7d7 100%);
                    border-color: #e53e3e;
                    color: #e53e3e;
                    transform: translateY(-2px);
                    box-shadow: 0 4px 12px rgba(229, 62, 62, 0.2);
                }

                &:hover:not(.ivu-radio-checked) {
                    background-color: #f8f9fa;
                    border-color: #ced4da;
                    transform: translateY(-1px);
                }
            }
        }
    }

    .remark-item {
        .remark-textarea {
            resize: vertical;
            min-height: 120px;
            border-radius: 10px;
            transition: all 0.3s ease;
            border: 1px solid #dee2e6;

            &:hover {
                border-color: #adb5bd;
            }

            &:focus-within {
                border-color: #4299e1;
                box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.1);
            }

            /deep/ textarea {
                border-radius: 10px;
                font-size: 14px;
                color: #2d3436;
                line-height: 1.6;
                transition: all 0.3s ease;
            }

            /deep/ textarea::placeholder {
                color: #adb5bd;
                font-style: italic;
            }
        }
    }
    .remark-item.ivu-form-item-error {
        .remark-textarea {
            /deep/ textarea {
                border-color: #ff4d4f;
                background-color: #fff2f0;
                box-shadow: 0 0 0 3px rgba(255, 77, 79, 0.1);
            }
        }
    }
}

/* Modal底部按钮 */
.modal-footer {
    padding: 20px 24px;
    border-top: 1px solid #e9ecef;
    background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
    display: flex;
    justify-content: flex-end;
    align-items: center;

    .cancel-btn {
        margin-right: 12px;
        padding: 8px 20px;
        border-radius: 8px;
        font-weight: 500;
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        
        &:hover {
            color: #495057;
            background-color: #f1f3f5;
            transform: translateY(-1px);
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }
        
        &:active {
            transform: translateY(0);
        }
    }

    .confirm-btn {
        min-width: 90px;
        padding: 8px 20px;
        border-radius: 8px;
        font-weight: 500;
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        
        &:hover:not(:disabled) {
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }
        
        &:active:not(:disabled) {
            transform: translateY(0);
        }
        
        &:disabled {
            opacity: 0.7;
            cursor: not-allowed;
        }
    }
}

/* 加载动画 */
.custom-spin {
    /deep/ .ivu-spin-main {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        height: 100%;
        background: rgba(255, 255, 255, 0.95);
        backdrop-filter: blur(4px);

        .spin-icon {
            animation: spin 1s linear infinite;
            font-size: 36px;
            color: #4299e1;
            margin-bottom: 12px;
            filter: drop-shadow(0 2px 4px rgba(66, 153, 225, 0.3));
        }
        
        div {
            font-size: 16px;
            color: #4a5568;
            font-weight: 500;
            letter-spacing: 0.5px;
        }
    }
}

@keyframes spin {
    from {
        transform: rotate(0deg);
    }
    to {
        transform: rotate(360deg);
    }
}
</style>
